/**
 * Settings API
 * Handles system settings management calls
 */
import apiClient from './apiClient';

export interface SystemSetting {
  id: number;
  key: string;
  value: any;
  data_type: 'string' | 'number' | 'boolean' | 'json';
  description: string | null;
  is_public: boolean;
  updated_by: number | null;
  updated_at: string | null;
}

export interface SettingsListResponse {
  settings: SystemSetting[];
  total: number;
}

export interface UpdateSettingData {
  value: any;
  description?: string;
  is_public?: boolean;
}

export interface BatchUpdateData {
  settings: Array<{ key: string; value: any }>;
}

export const settingsApi = {
  /**
   * Get all settings (non-admins only see public settings)
   */
  getSettings: async (): Promise<SettingsListResponse> => {
    return apiClient.get<SettingsListResponse>('/settings');
  },

  /**
   * Get a specific setting by key
   */
  getSetting: async (key: string): Promise<{ setting: SystemSetting }> => {
    return apiClient.get<{ setting: SystemSetting }>(`/settings/${encodeURIComponent(key)}`);
  },

  /**
   * Update a setting
   */
  updateSetting: async (key: string, data: UpdateSettingData): Promise<{ message: string; setting: SystemSetting }> => {
    return apiClient.put<{ message: string; setting: SystemSetting }>(`/settings/${encodeURIComponent(key)}`, data);
  },

  /**
   * Update multiple settings at once
   */
  updateSettingsBatch: async (data: BatchUpdateData): Promise<{ message: string; updated: string[] }> => {
    return apiClient.put<{ message: string; updated: string[] }>('/settings/batch', data);
  },

  /**
   * Initialize default settings
   */
  initializeSettings: async (): Promise<{ message: string; initialized: string[] }> => {
    return apiClient.post<{ message: string; initialized: string[] }>('/settings/initialize');
  },

  /**
   * Seed GreytHR settings from environment variables
   * Super Admin only
   */
  seedGreythrSettings: async (): Promise<{ message: string; updated: string[] }> => {
    return apiClient.post<{ message: string; updated: string[] }>('/settings/seed-greythr');
  },
};

export default settingsApi;
